import { PageShell } from "@/components/layout/PageShell";
import { PriorityReportsModal } from "@/components/priorities/PriorityReportsModal";
import { ManageTeamDialog } from "@/components/team/ManageTeamDialog";
import { TeamListSkeleton } from "@/components/team/TeamListSkeleton";
import { TeamMemberSection } from "@/components/team/TeamMemberSection";
import { TeamPageHeader } from "@/components/team/TeamPageHeader";
import { TeamPriorityFilter } from "@/components/team/TeamPriorityFilter";
import { Button } from "@/components/ui/button";
import { TeamHealthMetrics } from "@/components/team/TeamWeekSummary";
import { UpdateFilter } from "@/components/team/teamFilterConstants";
import { useIsLeader } from "@/hooks/useIsLeader";
import { useTeamPriorityGroups } from "@/hooks/useTeamPriorityGroups";
import { useTeamUpdateStatus } from "@/hooks/useTeamUpdateStatus";
import { cn } from "@/lib/utils";
import { OrgMember, Priority } from "@/types";
import { Settings } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const MyTeam = () => {
  const { user, isLeader, isLoading: isLoadingLeader, directReports } = useIsLeader();

  // Its own query, independent of the status-filter chips below: whether someone updated this week
  // is a fact about the person, not about which priority statuses happen to be on screen.
  const {
    weekNumber: updateStatusWeek,
    members: updateStatusMembers,
    isLoading: isLoadingUpdateStatus,
  } = useTeamUpdateStatus();

  const updateStatusByPid = useMemo(
    () => new Map(updateStatusMembers.map((m) => [m.pid, m])),
    [updateStatusMembers],
  );

  // Memoize reports to prevent unnecessary re-renders
  const reports = useMemo(() => directReports ?? [], [directReports]);
  const teamMembers = useMemo(() => {
    if (!user) {
      return reports;
    }

    // The direct-reports API excludes the leader, but this page should show the full team view including them.
    const currentUser: OrgMember = {
      pid: user.pid,
      fullName: user.fullName,
      email: user.email,
      avatarUrl: user.avatarUrl,
      position: user.position,
      managerPid: user.managerPid,
      reportCount: reports.length,
    };

    return [currentUser, ...reports];
  }, [reports, user]);

  // State for reports modal
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>("");
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);

  // State for manage team dialog
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);

  useEffect(() => {
    if (!isLeader) setIsManageDialogOpen(false);
  }, [isLeader]);

  const [statusFilter, setStatusFilter] = useState<string[]>(["focus", "queue", "blocked", "waiting"]);
  const [updateFilter, setUpdateFilter] = useState<UpdateFilter>("all");

  const handlePriorityClick = (priority: Priority, member: OrgMember) => {
    setSelectedPriority(priority);
    setSelectedUserName(member.fullName);
    setIsReportsModalOpen(true);
  };

  const toggleStatus = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  };

  const {
    groups: prioritiesByMember,
    totalPriorities,
    isLoading: isLoadingPriorities,
  } = useTeamPriorityGroups({ teamMembers, statusFilter, updateFilter, updateStatusByPid });

  const isLoading = isLoadingLeader || isLoadingPriorities;
  const hasReports = reports.length > 0;

  return (
    <PageShell>
      {/* Identity + health metrics live in one block. The two warning pills are hidden for someone
          with no direct reports — there is no team update-health to report on, and "1 member needs
          update" about yourself is noise. */}
      <TeamPageHeader
        isLeader={isLeader}
        isLoading={isLoading}
        weekNumber={updateStatusWeek}
        memberCount={teamMembers.length}
        totalPriorities={totalPriorities}
        metrics={
          <TeamHealthMetrics
            members={updateStatusMembers}
            showTeamHealth={hasReports}
            updateFilter={updateFilter}
            onUpdateFilterChange={setUpdateFilter}
            isLoading={hasReports && isLoadingUpdateStatus}
          />
        }
      />

      {/* Toolbar: status chips + Manage Team, as a separate row below the identity block so
          filters/actions never compete with the team-health read. */}
      {!isLoading && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card/50 px-4 py-3">
          <TeamPriorityFilter
            value={statusFilter}
            onToggle={toggleStatus}
            onClear={() => setStatusFilter([])}
          />

          {isLeader && (
            <Button
              onClick={() => setIsManageDialogOpen(true)}
              size="sm"
              variant="outline"
              className="shrink-0 gap-2 rounded-full px-4"
            >
              <Settings className="h-4 w-4" />
              Manage Team
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <TeamListSkeleton />
      ) : (
        <div className={cn("space-y-6 transition-opacity duration-200", isLoadingPriorities && "opacity-50")}>
          {Object.values(prioritiesByMember).map(({ member, priorities }) => (
            <TeamMemberSection
              key={member.pid}
              member={member}
              priorities={priorities}
              memberUpdate={updateStatusByPid.get(member.pid)}
              isSelf={member.pid === user?.pid}
              hasStatusFilter={statusFilter.length > 0}
              onPriorityClick={(priority) => handlePriorityClick(priority, member)}
            />
          ))}

          {/* Only for an active people-filter — an empty team is not a "no matches" case. */}
          {updateFilter !== "all" && Object.keys(prioritiesByMember).length === 0 && (
            <div className="rounded-xl border border-dashed border-border/50 bg-muted/20 p-10 text-center">
              <p className="text-sm font-medium text-muted-foreground">
                No team members match this filter
              </p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                {updateFilter === "needs-update"
                  ? "Everyone has posted an update this week."
                  : "Everyone currently holds a full Focus/Queue board."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Reports Modal */}
      <PriorityReportsModal
        priority={selectedPriority}
        userName={selectedUserName}
        open={isReportsModalOpen}
        onOpenChange={setIsReportsModalOpen}
      />

      {isLeader && (
        <ManageTeamDialog
          open={isManageDialogOpen}
          onClose={() => setIsManageDialogOpen(false)}
          currentReports={reports}
        />
      )}
    </PageShell>
  );
};

export default MyTeam;
